/**
 * True when this window was loaded by Microsoft returning a popup or silent-iframe sign-in response, which the
 * MSAL v5 redirect bridge must hand back to the main window instead of the app bootstrapping here.
 *
 * window.opener can't be used for this: the Microsoft sign-in page's Cross-Origin-Opener-Policy severs it, so the
 * response is recognised by MSAL's own state parameter (base64url JSON with `id` and `meta.interactionType`).
 * Redirect responses return false, since the app's handleRedirectObservable processes those.
 *
 * Kept free of Angular and MSAL imports: main.ts calls it before deciding whether to bootstrap.
 */
export function isMsalBridgeResponse(hash: string, search: string): boolean {
	return [hash, search].some((part: string) => isBridgeParams(new URLSearchParams(part.replace(/^[#?]/, ''))));
}

function isBridgeParams(params: URLSearchParams): boolean {
	const state: string | null = params.get('state');
	if (!state || !(params.has('code') || params.has('error'))) {
		return false;
	}
	try {
		const libraryState: { id?: unknown; meta?: { interactionType?: unknown } } = JSON.parse(
			decodeBase64Url(state.split('|')[0])
		);
		const interactionType: unknown = libraryState.meta?.interactionType;
		return !!libraryState.id && typeof interactionType === 'string' && interactionType !== 'redirect';
	} catch {
		// Not MSAL state (another provider's code/state pair, or malformed)
		return false;
	}
}

function decodeBase64Url(value: string): string {
	const base64: string = value.replace(/-/g, '+').replace(/_/g, '/');
	const padded: string = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
	return new TextDecoder().decode(Uint8Array.from(atob(padded), (char: string) => char.codePointAt(0) ?? 0));
}
